import { useEffect, useMemo, useRef } from 'react'

import Sortable from 'sortablejs'

export function useSortableProgressChat({
  dropRef,
  itemCount,
  onSort,
}: {
  dropRef: React.RefObject<HTMLElement>
  itemCount: number
  onSort: (ids: string[]) => void
}) {
  const sortableRef = useRef<Sortable | null>(null)
  const boundElRef = useRef<HTMLElement | null>(null)
  const onSortRef = useRef(onSort)
  onSortRef.current = onSort

  const getId = useMemo(() => {
    const map = new WeakMap<object, string>()
    return (item: any) => {
      if (item?.id != null) return String(item.id)
      if (!map.has(item)) {
        const id =
          globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2)
        map.set(item, id)
      }
      return map.get(item)!
    }
  }, [])

  useEffect(() => {
    const el = dropRef.current

    // no element yet
    if (!el) {
      if (sortableRef.current) {
        sortableRef.current.destroy()
        sortableRef.current = null
        boundElRef.current = null
      }
      return
    }

    // same element still mounted, nothing to do
    if (boundElRef.current === el && sortableRef.current) {
      return
    }

    // element changed -> destroy old sortable first
    if (sortableRef.current) {
      sortableRef.current.destroy()
      sortableRef.current = null
      boundElRef.current = null
    }

    sortableRef.current = new Sortable(el, {
      animation: 180,
      draggable: '.drop-card:not(.no-drag)',
      dataIdAttr: 'data-id',

      delay: 180,
      delayOnTouchOnly: true,
      touchStartThreshold: 6,
      fallbackTolerance: 6,

      forceFallback: true,
      fallbackOnBody: true,

      filter:
        '.no-drag, [data-no-drag="true"], button, a, input, textarea, .uploadedfile__remove, .uploadedfile__premium__icon, .uploadedfile__premium, .uploadedfile__preview, .uploadedfile__cover, .uploadedfile__cover__text, .uploadedfile__play',
      preventOnFilter: false,

      ghostClass: 'cm-sort-ghost',
      chosenClass: 'cm-sort-chosen',
      dragClass: 'cm-sort-drag',

      onEnd: () => {
        const ids = sortableRef.current?.toArray() ?? []
        onSortRef.current(ids)
      },
    })

    boundElRef.current = el

    return () => {
      if (sortableRef.current && boundElRef.current === el) {
        sortableRef.current.destroy()
        sortableRef.current = null
        boundElRef.current = null
      }
    }
  })

  useEffect(() => {
    if (!sortableRef.current) return
    sortableRef.current.option('disabled', itemCount <= 1)
  }, [itemCount])

  return { getId }
}